import { createSlice, type EntityState, type PayloadAction } from '@reduxjs/toolkit';

import { getWorkspaceMatchFulfilled } from '@/api/workspace/index.ts';
import { repriceQuotesTilesReducer } from '@/store/blotter/workflows/cashRepriceQuote/extraReducers';
import { summonOrderTilesReducer } from '@/store/blotter/workflows/summonOrder/extraReducers.ts';
import { reduceTileOnFetchSharedWorkspace } from '@/store/sharedWorkspace/workflows/getSharedTab.workflow.ts';
import { reduceTilesStateOnSharedTabRemoved } from '@/store/sharedWorkspace/workflows/onSharedTabRemoved.workflow.ts';
import { removeTab } from '@/store/tabs/slice.ts';
import type { Tab } from '@/store/tabs/types.ts';
import { tilesAdapter, tilesAdapterSelectors } from '@/store/tiles/adapter.ts';
import { compactLayout, convertLayoutBackToTiles, convertTilesToLayout } from '@/store/tiles/compaction.ts';
import type { Tile } from '@/store/tiles/types.ts';
import type { UserPreferencesState } from '@/store/userPreferences/state.ts';

export const tilesSlice = createSlice({
  name: 'tiles',
  initialState: tilesAdapter.getInitialState(),

  reducers: {
    addTile: (
      state,
      { payload: { tile } }: PayloadAction<{ tile: Tile; userPreferences: UserPreferencesState; sessionId: string }>,
    ) => {
      tilesAdapter.addOne(state, tile);
      compactTiles(state, tile.tabId, tile.tileId);
    },

    duplicateTile: (
      state,
      {
        payload: { sourceTileId, newTileId },
      }: PayloadAction<{ sourceTileId: string; newTileId: string; sessionId?: string }>,
    ) => {
      const sourceTile = state.entities[sourceTileId];

      if (!sourceTile) {
        return;
      }

      const { productType, tabId, x, y, w, h } = sourceTile;

      const newTile: Tile = {
        tileId: newTileId,
        productType,
        tabId,
        x: x + w,
        y,
        h,
        w,
      };

      tilesAdapter.addOne(state, newTile);

      compactTiles(state, tabId, newTileId);
    },

    removeTile: (state, { payload: tileId }: PayloadAction<string>) => {
      const tile = state.entities[tileId];
      if (!tile) {
        return;
      }

      tilesAdapter.removeOne(state, tileId);

      compactTiles(state, tile.tabId);
    },

    updateTileProductType: (
      state,
      {
        payload,
      }: PayloadAction<{
        tileId: string;
        productType: Tile['productType'];
        userPreferences: UserPreferencesState;
        sessionId: string;
        currencyPair?: string;
        overriddenClientId?: number;
      }>,
    ) => {
      tilesAdapter.updateOne(state, { id: payload.tileId, changes: { productType: payload.productType } });
    },

    resizeTile: (state, { payload: { tileId, w, h } }: PayloadAction<{ tileId: string; w?: number; h?: number }>) => {
      const tile = state.entities[tileId];
      if (!tile) {
        return;
      }

      const requestedH = h ?? tile.h;
      const requestedW = w ?? tile.w;

      if (tile.h === requestedH) {
        if (tile.w === requestedW) {
          console.warn('action resizeTile was dispatched with nilpotent data');
          return;
        }
        // only w differs, applying
        tile.w = requestedW;
      } else {
        // h differs, applying
        tile.h = requestedH;
        if (tile.w === requestedW) {
          // w differs too, applying
          tile.w = requestedW;
        }
      }

      compactTiles(state, tile.tabId, tileId);
    },

    moveTile: (state, { payload }: PayloadAction<{ tileId: string; x: number; y: number }>) => {
      const tile = tilesAdapter.getSelectors().selectById(state, payload.tileId);

      if (!tile) {
        return;
      }

      tilesAdapter.updateOne(state, { id: payload.tileId, changes: { x: payload.x, y: payload.y } });

      // compacting, making the moved tile static in the layout
      // so that it appears at the same place it was displayed realtime during drag
      //  - making this tile static make possible to move a tile to the first "row" of the grid
      //  - always displays the dragged tile where the mouse is (no buggy jump)
      compactTiles(state, tile.tabId, payload.tileId);

      // compacting a second time to remove the holes that the first compaction could a made
      // since the drag is over and no realtime feedback is mandatory
      compactTiles(state, tile.tabId);
    },
  },

  extraReducers: (builder) => {
    builder.addCase(removeTab, (state, { payload: tabId }: PayloadAction<Tab['id']>) => {
      const tilesToRemove = Object.values(state.entities).filter((tile) => tile.tabId === tabId);
      const tileIdsToRemove = tilesToRemove.map((tile) => tile.tileId);
      tilesAdapter.removeMany(state, tileIdsToRemove);
    });

    reduceTilesStateOnSharedTabRemoved(builder);
    summonOrderTilesReducer(builder);
    reduceTileOnFetchSharedWorkspace(builder);

    builder.addMatcher(getWorkspaceMatchFulfilled, (state, action) => {
      if (!action.payload) {
        return;
      }

      tilesAdapter.removeAll(state); // clear all tiles created before workspace loaded
      tilesAdapter.addMany(state, action.payload.tiles);
    });
    repriceQuotesTilesReducer(builder);
  },

  selectors: {
    selectTilesState: (state) => state,
    selectAllTiles: tilesAdapterSelectors.selectAll,
    selectTileW: (state, tileId: string) => tilesAdapterSelectors.selectById(state, tileId)?.w,
    // returns a mapping between tileIds and the their height in cell height unit
    selectTileHeights: (state) =>
      tilesAdapterSelectors.selectAll(state).reduce(
        (acc, tile) => {
          acc[tile.tileId] = tile.h;
          return acc;
        },
        {} as Record<string, number>,
      ),
  },
});

export const { addTile, duplicateTile, removeTile, updateTileProductType, resizeTile, moveTile } = tilesSlice.actions;

export const { selectTilesState, selectAllTiles } = tilesSlice.selectors;

export type TileState = ReturnType<typeof tilesSlice.getInitialState>;

export function compactTiles(state: EntityState<Tile, string>, tabId: string, staticTileId?: string) {
  const allTilesOfTab = Object.values(state.entities).filter((tile) => tile.tabId === tabId);

  const layout = convertTilesToLayout(allTilesOfTab);
  const compactedLayout = compactLayout(layout.items, staticTileId);
  const updatedTiles = convertLayoutBackToTiles({
    sourceTiles: layout.sourceTiles,
    items: compactedLayout,
  });

  tilesAdapter.updateMany(
    state,
    updatedTiles.map((tile) => ({ id: tile.tileId, changes: { x: tile.x, y: tile.y } })),
  );
}
